import { useCallback, useEffect, useSyncExternalStore } from 'react';
import { safeStorage } from './utils';

/**
 * Theme: System / Light / Dark, persisted in localStorage (`af:theme`) and applied as `data-theme` on <html>.
 * `public/theme-init.js` runs the same logic before first paint (no flash); keep both in sync.
 * Optional: `af:term-dark` keeps terminal panes dark while the rest of the app is light (`data-term="dark"`).
 */
export type ThemePref = 'system' | 'light' | 'dark';
export type Theme = 'light' | 'dark';

export const THEME_KEY = 'af:theme';
export const TERM_DARK_KEY = 'af:term-dark';
export const THEME_PREFS: readonly ThemePref[] = ['system', 'light', 'dark'];

const LIGHT_QUERY = '(prefers-color-scheme: light)';

function systemTheme(): Theme {
  try {
    return window.matchMedia?.(LIGHT_QUERY).matches ? 'light' : 'dark';
  } catch {
    return 'dark';
  }
}

export function readThemePref(): ThemePref {
  const v = safeStorage.get<string>(THEME_KEY);
  return v === 'light' || v === 'dark' ? v : 'system';
}

export function resolveTheme(pref: ThemePref): Theme {
  return pref === 'system' ? systemTheme() : pref;
}

const listeners = new Set<() => void>();
const emit = () => {
  for (const l of listeners) l();
};

/** Write `data-theme` (+ color-scheme/theme-color metas) for a preference. */
export function applyTheme(pref: ThemePref = readThemePref()): Theme {
  const theme = resolveTheme(pref);
  const root = document.documentElement;
  root.dataset.theme = theme;
  root.dataset.themePref = pref;
  if (safeStorage.get<boolean>(TERM_DARK_KEY) === true) root.dataset.term = 'dark';
  else delete root.dataset.term;
  document.querySelector('meta[name="color-scheme"]')?.setAttribute('content', theme);
  document
    .querySelector('meta[name="theme-color"]')
    ?.setAttribute('content', theme === 'dark' ? '#0a0a0b' : '#ffffff');
  return theme;
}

/** Called once at boot (the inline-free init script already did this; this keeps them consistent). */
export function applyStoredTheme() {
  applyTheme();
}

export function setThemePref(pref: ThemePref) {
  if (pref === 'system') safeStorage.remove(THEME_KEY);
  else safeStorage.set(THEME_KEY, pref);
  applyTheme(pref);
  emit();
}

export function setTerminalDark(on: boolean) {
  if (on) safeStorage.set(TERM_DARK_KEY, true);
  else safeStorage.remove(TERM_DARK_KEY);
  applyTheme();
  emit();
}

function subscribe(cb: () => void) {
  listeners.add(cb);
  return () => listeners.delete(cb);
}
const snapshot = () =>
  `${document.documentElement.dataset.themePref ?? readThemePref()}|${document.documentElement.dataset.theme ?? 'dark'}|${document.documentElement.dataset.term ?? ''}`;

/** Current preference, resolved theme and setters. Re-renders on changes and on OS changes in System mode. */
export function useTheme() {
  const snap = useSyncExternalStore(subscribe, snapshot, () => 'system|dark|');
  const [prefRaw, themeRaw, term] = snap.split('|');
  const pref: ThemePref = prefRaw === 'light' || prefRaw === 'dark' ? prefRaw : 'system';
  const theme: Theme = themeRaw === 'light' ? 'light' : 'dark';

  useEffect(() => {
    if (pref !== 'system' || !window.matchMedia) return;
    const mql = window.matchMedia(LIGHT_QUERY);
    const onChange = () => {
      applyTheme('system');
      emit();
    };
    mql.addEventListener?.('change', onChange);
    return () => mql.removeEventListener?.('change', onChange);
  }, [pref]);

  const setPref = useCallback((p: ThemePref) => setThemePref(p), []);
  const toggle = useCallback(() => setThemePref(theme === 'dark' ? 'light' : 'dark'), [theme]);
  const setTermDark = useCallback((on: boolean) => setTerminalDark(on), []);
  return { pref, theme, setPref, toggle, terminalDark: term === 'dark', setTerminalDark: setTermDark };
}
